import { PortalSystemOption, RetrievalFieldOption } from 'services/api/portalSystems';

// The published workflow definition WEB-0 drives end-to-end (seeded on the stack).
export const REFERRAL_INTAKE_API_NAME = 'referral_intake';
// The second workflow type: integration-triggered portal pulls that spawn
// or update referral-intake runs. Surfaced as Tasks, never as a referral.
export const PORTAL_AUTOMATION_API_NAME = 'portal_automation';
// The third: a portal-wide sweep for new referral activity. Anchored to a source_system rather
// than a referral, so it carries no patient — surfaced as a Portal Scan.
export const PORTAL_DISCOVERY_API_NAME = 'portal_discovery';

// Both portal work types. Anything portal-shaped — excluded from the referral inbox — keys off
// this set rather than testing the two names separately, so a future portal type is one entry
// rather than a hunt for equality checks.
export const PORTAL_API_NAMES: string[] = [PORTAL_AUTOMATION_API_NAME, PORTAL_DISCOVERY_API_NAME];

export function isPortalWorkflow(apiName: string): boolean {
  return PORTAL_API_NAMES.includes(apiName);
}

// What a sweep reads out of EACH ROW it finds — sent to the extension as the job's `itemFields`
// (OLIO-3023). Olio names what a run collects; the extension has never invented field names and
// carries this same list as a fallback for jobs from builds that predate the plumbing. Sending
// it is what lets the list change without shipping an extension.
//
// `facility_name` is the addition: the fallback has no facility, so the results table's SNF
// column can only be populated by a sweep recorded (or re-recorded) after this ships. Existing
// recordings keep replaying their own captured fields and render an em dash there — they do not
// break, and nothing here obliges anyone to re-record.
//
// Snake_case verbatim: these are the item shape's own key names, not API field keys.
export const SWEEP_ITEM_FIELDS = [
  'reference_or_url',
  'patient_name',
  'patient_dob',
  'facility_name',
  'last_updated',
  'unread_documents',
];

export const INBOUND_REFERRALS_ROUTES = {
  BASE: '/inbound-referrals',
  // Create + edit are modals now (ReferralModal), not routed pages.
};

// The window.postMessage handshake with the Olio browser extension. These
// mirror the extension's constants exactly — both sides post on our own page's window.
export const PORTAL_RETRIEVAL_MESSAGES = {
  request: 'olio:portal-retrieval',
  ack: 'olio:portal-retrieval-ack',
  result: 'olio:portal-retrieval-result',
  probe: 'olio:portal-retrieval-probe',
  status: 'olio:portal-retrieval-status',
  restart: 'olio:portal-retrieval-restart',
  // Manual mode (OLIO-2703): open the portal tab for the user to read, no script.
  // The extension owns the launched tab and closes it on manualClose (OLIO-2704).
  manualLaunch: 'olio:portal-manual-launch',
  manualLaunchAck: 'olio:portal-manual-launch-ack',
  manualClose: 'olio:portal-manual-close',
} as const;

// Extension auth handoff message types. Both sides (web + extension) share these strings.
export const EXTENSION_AUTH_MESSAGES = {
  handoff: 'olio:token-handoff',
  handoffRequest: 'olio:token-handoff-request',
} as const;

// Extension IDs, pinned by the `key` in each build's manifest (OLIO-3080). The
// extension now ships as TWO separately-keyed builds so real prod trusts only
// app.olio.health and is a distinct identity:
//   - prod build     → app.olio.health
//   - non-prod build → staging.olio.health, load-test.olio.health, localhost:3001
// The SPA must hand off to the ID whose build trusts THIS page's origin, or the
// externally_connectable message silently reaches the wrong (or no) extension.
export const OLIO_EXTENSION_ID_PROD = 'odljdkceepileknakfaiboboigggggbo';
export const OLIO_EXTENSION_ID_NONPROD = 'oajilcbfhlfhbljidnfeoecbmllpcofl';

// Web origins each build trusts — mirrors the extension's own allowlist
// (src/lib/origins.ts over there). Exact origins, no trailing slash.
const PROD_ORIGINS = ['https://app.olio.health'];
const NONPROD_ORIGINS = ['https://staging.olio.health', 'https://load-test.olio.health', 'http://localhost:3001'];

// The extension ID whose build trusts `origin`, or undefined when no extension
// build claims this origin (e.g. a PR preview host) — callers then no-op cleanly
// rather than messaging an unrelated extension.
export function extensionIdForOrigin(origin: string = window.location.origin): string | undefined {
  if (PROD_ORIGINS.includes(origin)) return OLIO_EXTENSION_ID_PROD;
  if (NONPROD_ORIGINS.includes(origin)) return OLIO_EXTENSION_ID_NONPROD;
  return undefined;
}

// Named port the SPA opens to the extension (chrome.runtime.connect) for renewal requests.
// Only the SPA can open an externally_connectable port to the pinned ID, so requests over
// it are authenticated — page scripts can't forge them the way they could a window message.
export const OLIO_EXTENSION_HANDOFF_PORT = 'olio-token-handoff';

// Portal names used to live here as a hardcoded map. They are server data now: every
// connection row carries its own `displayName` (GET /portal_connections), so end-user
// surfaces read a name through hooks/usePortalConnections' usePortalName rather than
// looking one up locally.

// The demographic value names this UI reads/writes — snake_case, sent verbatim. They are
// the seed's value names (app/seeds/seed_data/workflows.rb), not API field names, so
// OliveBranch leaves them alone. (The packet/attachment value names live in actionConfig's
// graph vocabulary — this UI uploads `attachments` but never reads attachment_ids /
// attachments_processed directly.)
export const VALUE_NAMES = {
  PATIENT_FIRST_NAME: 'patient_first_name',
  PATIENT_LAST_NAME: 'patient_last_name',
  // Optional to the engine, but one of only TWO ways to satisfy MatrixCare's "one field beyond the
  // minimum four" rule, and now ENFORCED at the click for a MatrixCare push (syncFieldErrors). The
  // other satisfier is the SSN, which face sheets routinely redact and which becomes a permanent
  // matching key at the vendor — so this is the remedy the modal highlights. Extraction never emits a
  // middle name (see the seed's own note on it), so it only ever arrives by hand, which is exactly why
  // that rule fires as often as it does.
  PATIENT_MIDDLE_NAME: 'patient_middle_name',
  // Portal-collection transport forms, never engine values (the match keys and value
  // catalog know only the first/last pair). `patient_name` is the composite a person or
  // the manual HUD fills — {first, last} when the portal splits them, {full} when it
  // shows one name; `patient_full_name` is the single string a saved script captures.
  // Workflow::NormalizeNameOutput resolves both server-side.
  //
  // NOT a middle-name source: NormalizeNameOutput folds middles into the FIRST name
  // ("Jane A Doe" -> first "Jane A"), so a collected name never satisfies the rule above.
  PATIENT_NAME: 'patient_name',
  PATIENT_FULL_NAME: 'patient_full_name',
  PATIENT_GENDER: 'patient_gender',
  PATIENT_DOB: 'patient_dob',
  // Extracted from the packet when it's stated, and enterable here. Shown in full only in the
  // entry form — everywhere it's *displayed* it's masked (see maskSsn).
  PATIENT_SSN: 'patient_ssn',
  // Needed only to push a patient INTO an EHR (OLIO-2846), not to work the referral, so it is not on
  // the referral entry form — it lives on the sync modal's field list instead. State is part of
  // MatrixCare's resident matching on EVERY push.
  //
  // `patient_mrn` was here too and is deliberately GONE. The MRN is the facility's permanent clinical
  // identifier, required only where a facility assigns them by hand — which nobody on this screen can
  // detect, so asking here was asking the wrong person. It is per-facility configuration in the
  // patient-sync settings now and the platform derives the value. olio-api still declares the value
  // name, so re-adding a field here would work if that ever becomes the right answer again.
  PATIENT_STATE: 'patient_state',
  PLAN_TYPE: 'plan_type',
  // The facilities the referral names — n-ary, CollectedFacility entries. Always sent at
  // retrieval completion, empty or not.
  FACILITIES: 'facilities',
  REFERRAL_SOURCE: 'referral_source',
  // Benefits-eligibility inputs (Stedi 270) — the payer, the rendering provider, and the
  // member id. Names match olio-api's VALUE_CATALOG (seed_data/workflows.rb); `payer_id` is
  // produced by PayerSearch (not user-editable), so it isn't a form field.
  MEMBER_ID: 'member_id',
  // The payer as named on ONE insurance (OLIO-2793) — distinct from plan_type, which is the
  // referral-wide payer the list's "Plan Type" column reads. Each coverage's payer search runs on
  // its own copy of this.
  PAYER_NAME: 'payer_name',
  PROVIDER_NPI: 'provider_npi',
  PROVIDER_ORGANIZATION_NAME: 'provider_organization_name',
} as const;

// What a field holds, which is what every surface renders it from. Beyond the plain widgets:
// 'name' is one field a person fills as first + last, or as a single name when that is all the
// portal shows; 'facility' is a pick from the user's own facilities. Both resolve server-side —
// a name to the engine's first/last pair, a facility to the Olio building it already names.
export type ReferralFieldType = 'text' | 'date' | 'select' | 'ssn' | 'name' | 'facility';

// Which group of properties a field belongs to on a by-property surface (the task slideout,
// OLIO-3022). Sections are a PRESENTATION grouping the API has no home for — the value catalog
// carries name/type/label and nothing about how a person reads them together.
// `Insurance Information` is drawn in the design but ships absent (PUNTED), so there are two.
export type ReferralFieldSection = 'demographics' | 'facility';

export const REFERRAL_FIELD_SECTIONS: ReferralFieldSection[] = ['demographics', 'facility'];

export const SECTION_LABELS: Record<ReferralFieldSection, string> = {
  demographics: 'Patient Demographics',
  facility: 'Facility',
};

export interface ReferralFieldDef {
  name: string;
  label: string;
  type: ReferralFieldType;
  // Where this field renders on a by-property surface, and in what order within its section.
  // Optional: a field with no section is declared for capture but not rendered by property
  // (plan_type, whose section is punted with the rest of Insurance Information).
  section?: ReferralFieldSection;
  order?: number;
  // How many values the field holds. Independent of the datatype, so a repeatable pick needs no
  // datatype of its own, and it is what tells a script's recorder to reach for an *_all verb.
  cardinality?: 'one' | 'many';
  optional?: boolean;
  // The alternative ways a *script* can capture this field, each naming the engine's own value
  // names — so a recorded script extracts into them directly and the DSL needs no new grammar.
  // Capturing every name in ANY ONE shape satisfies the field; the shapes are exclusive, which
  // is the whole point of the name datatype. A person fills the field itself (`name`); a script
  // fills one shape of it. `when` describes the portal that calls for that shape.
  captureShapes?: { shape: string; names: string[]; when: string }[];
}

// Patient Demographics — extract_attributes produces these and a person can also seed/edit
// them. `label` is the entry-form label; rendered values prefer the API's value label. Sex is
// a select rendered with the shared GENDERS options (same as /patients/new).
export const DEMOGRAPHIC_FIELDS: ReferralFieldDef[] = [
  { name: VALUE_NAMES.PATIENT_FIRST_NAME, label: 'First name', type: 'text' },
  // Optional, and on the referral form as well as the sync modal — unlike State and MRN (which are
  // vendor concerns and live only on the sync modal's list), a middle name is a fact about the patient.
  { name: VALUE_NAMES.PATIENT_MIDDLE_NAME, label: 'Middle name', type: 'text', optional: true },
  { name: VALUE_NAMES.PATIENT_LAST_NAME, label: 'Last name', type: 'text' },
  { name: VALUE_NAMES.PATIENT_GENDER, label: 'Sex', type: 'select' },
  { name: VALUE_NAMES.PATIENT_DOB, label: 'Date of birth', type: 'date' },
  // Optional, and the one place the full number is shown: a person can't confirm or correct a
  // number they can't read. Every read-only surface masks it.
  // Its own type because it's the one field with a shape to hold to — see ssn.ts. Whatever a packet
  // states, what leaves this form is nine digits or nothing.
  { name: VALUE_NAMES.PATIENT_SSN, label: 'Social Security Number', type: 'ssn', optional: true },
];

// Insurance Information — the values that genuinely belong to the REFERRAL rather than to one
// insurance. plan_type is the referral-wide payer backing the list's "Plan Type" column; the provider
// fields are the Stedi 270's rendering provider, the same whoever is being billed. payer_id is
// produced by PayerSearch, not entered.
//
// `member_id` is deliberately absent (OLIO-2793). It belongs to one INSURANCE now rather than to the
// referral, and a value that belongs to an insurance is never read off the referral — so a field
// here would accept a member ID, save without error, and have it apply to nothing. Editing one per
// insurance is the coverage tabs' Edit Details (OLIO-2794); on create, the insurance step already
// writes one member ID per insurance through `coverages`. Older referrals are the exception — see
// REFERRAL_LEVEL_MEMBER_ID_FIELD.
// The rendering provider on the 270 — whoever is being billed, the same for every insurance on the
// referral. Its own list because CREATE offers these two and not plan_type: the create step already
// asks for each insurance's payer by name, and plan_type's label is "Payer Name", so offering it
// there would put two differently-meaning "Payer Name" fields on one screen. Create derives
// plan_type from the primary insurance's payer instead (`insuranceValues`).
//
// Both are asked for because nothing else produces them and the verification requires them: a
// referral created without them starts with every Benefit Verification flagged. The plan is for the
// API to derive them from the referral's group, at which point these two come off the form.
export const PROVIDER_FIELDS: ReferralFieldDef[] = [
  { name: VALUE_NAMES.PROVIDER_NPI, label: 'Provider NPI', type: 'text', optional: true },
  { name: VALUE_NAMES.PROVIDER_ORGANIZATION_NAME, label: 'Provider Organization', type: 'text', optional: true },
];

export const INSURANCE_FIELDS: ReferralFieldDef[] = [
  { name: VALUE_NAMES.PLAN_TYPE, label: 'Payer Name', type: 'text', optional: true },
  ...PROVIDER_FIELDS,
];

// The one Member ID that belongs to the WHOLE REFERRAL rather than to a single insurance — offered
// only to referrals that still work that way.
//
// A referral keeps the configuration it started on for life, so referrals created before insurance
// checks went per-insurance still hold their member ID on the referral, and their verification still
// asks for it there. Removing this field outright would leave those referrals asking for a member ID
// that no screen can supply, permanently: the coverage tabs can't help them either, because they
// have no per-insurance records to edit. That covers referrals already open when the change ships,
// and any created if this bundle goes out ahead of the API.
//
// Deciding it per referral rather than per deploy is also what lets this bundle ship before or after
// the API rather than in lockstep.
//
// SAFE TO DELETE once no unfinished referral still asks for the member ID on the referral itself.
// Edit isn't offered on a finished referral, so at that point nothing can render this. The check,
// run against the API:
//
//   Workflow.where(api_name: 'referral_intake').where.not(status: %w[completed canceled])
//     .reject { |w| w.definition.facet_dimension?('coverage') }.count  # => 0
//
// Delete this constant and the `needsReferralLevelMemberId` branch in ReferralModal together.
export const REFERRAL_LEVEL_MEMBER_ID_FIELD: ReferralFieldDef = {
  name: VALUE_NAMES.MEMBER_ID,
  label: 'Member ID',
  type: 'text',
  optional: true,
};

// The render/capture metadata for every field portal retrieval CAN collect: one typed entry per
// value, whichever of the four modes fills it — the retrieval form, the extension's manual HUD, a
// recorded script, or the AI recorder. Every mode projects from this list and nothing else, so a
// field arrives here once and shows up in all four. WHICH of these a given portal actually
// collects is admin configuration served on the registry payload (OLIO-3074) — see
// retrievalFieldsFor / activeCollectNames below; this list carries what the api cannot: sections,
// ordering, and capture shapes. Optional throughout: retrieval stays document-first, and anything
// not collected is extracted from the documents downstream (collected values outrank extraction,
// so what someone read off the portal is never overwritten).
// The referral-intake entry forms are a different surface and keep DEMOGRAPHIC_FIELDS' plain
// first/last text fields.
export const PORTAL_COLLECT_FIELDS: ReferralFieldDef[] = [
  {
    name: VALUE_NAMES.FACILITIES,
    label: 'Facilities',
    type: 'facility',
    cardinality: 'many',
    optional: true,
    section: 'facility',
    order: 1,
  },
  {
    name: VALUE_NAMES.PATIENT_NAME,
    label: 'Patient name',
    type: 'name',
    optional: true,
    section: 'demographics',
    order: 1,
    captureShapes: [
      {
        shape: 'parts',
        names: [VALUE_NAMES.PATIENT_FIRST_NAME, VALUE_NAMES.PATIENT_LAST_NAME],
        when: 'the portal shows first and last separately',
      },
      { shape: 'single', names: [VALUE_NAMES.PATIENT_FULL_NAME], when: 'the portal shows one name field' },
    ],
  },
  { name: VALUE_NAMES.PATIENT_GENDER, label: 'Sex', type: 'select', optional: true, section: 'demographics', order: 3 },
  {
    name: VALUE_NAMES.PATIENT_DOB,
    label: 'Date of birth',
    type: 'date',
    optional: true,
    section: 'demographics',
    order: 4,
  },
  {
    name: VALUE_NAMES.PATIENT_SSN,
    label: 'Social Security Number',
    type: 'ssn',
    optional: true,
    section: 'demographics',
    order: 5,
  },
  // No section: `Payer Name` belongs to Insurance Information, which ships absent (PUNTED). The
  // field stays declared — retrieval still collects it — it simply renders on no section.
  { name: VALUE_NAMES.PLAN_TYPE, label: 'Payer Name', type: 'text', optional: true },
];

// A portal's configured retrieval field set, resolved against the registry payload. Falls back
// to the pre-config default selection (everything here but SSN) while the registry loads or for
// a system it doesn't know — the form must never render empty on a slow fetch.
const DEFAULT_COLLECT_NAMES = PORTAL_COLLECT_FIELDS.filter((field) => field.name !== VALUE_NAMES.PATIENT_SSN).map(
  (field) => field.name
);

export function activeCollectNames(systems: PortalSystemOption[], sourceSystem: string): string[] {
  const system = systems.find((option) => option.systemName === sourceSystem);
  return system ? system.retrievalFields.map((field) => field.name) : DEFAULT_COLLECT_NAMES;
}

export function retrievalFieldsFor(systems: PortalSystemOption[], sourceSystem: string): RetrievalFieldOption[] {
  const system = systems.find((option) => option.systemName === sourceSystem);
  if (system) return system.retrievalFields;

  return PORTAL_COLLECT_FIELDS.filter((field) => DEFAULT_COLLECT_NAMES.includes(field.name)).map(
    ({ name, label, type, cardinality }) => ({
      name,
      label,
      type,
      required: false,
      ...(cardinality === 'many' ? { cardinality } : {}),
    })
  );
}

// The sweep-item field list a scan job captures, per portal (OLIO-3074); SWEEP_ITEM_FIELDS is the
// pre-config fallback.
export function scanItemFieldsFor(systems: PortalSystemOption[], sourceSystem: string): string[] {
  const system = systems.find((option) => option.systemName === sourceSystem);
  return system ? system.scanFields.map((field) => field.name) : SWEEP_ITEM_FIELDS;
}

// The sweep-results-table columns, per portal: the configured column names with their labels.
const DEFAULT_SWEEP_TABLE_COLUMNS = [
  { name: 'patient_name', label: 'Patient Name' },
  { name: 'patient_dob', label: 'Date of Birth' },
  { name: 'facility_name', label: 'SNF' },
];

export function sweepTableColumnsFor(
  systems: PortalSystemOption[],
  sourceSystem: string
): { name: string; label: string }[] {
  const system = systems.find((option) => option.systemName === sourceSystem);
  if (!system || system.sweepTableFields.length === 0) return DEFAULT_SWEEP_TABLE_COLUMNS;

  return system.sweepTableFields.map((name) => ({
    name,
    label: system.scanFields.find((field) => field.name === name)?.label ?? name,
  }));
}

// One row on a by-property surface. A ROW is not always a FIELD: `patient_name` is one field a
// person fills, but the engine holds a first/last pair and the design draws two rows with two
// independent validity marks — so a name field expands into its `parts` capture shape, each part
// carrying the composite it writes back through.
//
// Deliberately derived from PORTAL_COLLECT_FIELDS rather than declared a second time: a field
// must arrive in one place (see the comment above that list), and a parallel row list would let
// the two disagree about what portal retrieval collects.
export interface PortalSectionField {
  // The engine value name this row reads and marks on.
  name: string;
  label: string;
  // The widget. A composite's parts are plain text, whatever the composite's own datatype is.
  type: ReferralFieldType;
  section: ReferralFieldSection;
  order: number;
  // Set when the row is one part of a transport composite: an edit posts under `name` with the
  // part filled in, and the api resolves it (Workflow::NormalizeNameOutput).
  composite?: { name: string; part: 'first' | 'last' };
}

// The part rows a name field expands into. Labels come from the intake declaration, which
// already names these two values for a person — nothing new is invented here.
const NAME_PART_ROWS: { name: string; part: 'first' | 'last' }[] = [
  { name: VALUE_NAMES.PATIENT_FIRST_NAME, part: 'first' },
  { name: VALUE_NAMES.PATIENT_LAST_NAME, part: 'last' },
];

function sectionRowsFor(field: ReferralFieldDef, section: ReferralFieldSection): PortalSectionField[] {
  const base = { section, order: field.order ?? 0 };
  if (field.type !== 'name') return [{ name: field.name, label: field.label, type: field.type, ...base }];

  return NAME_PART_ROWS.map(({ name, part }, index) => ({
    name,
    label: DEMOGRAPHIC_FIELDS.find((demographic) => demographic.name === name)?.label ?? field.label,
    type: 'text' as const,
    ...base,
    // Parts share their field's order and are separated by their position within it, so the pair
    // stays adjacent however the rest of the section is ordered.
    order: base.order + index / 10,
    composite: { name: field.name, part },
  }));
}

// The rows one section renders, in declared order. `activeNames` scopes the rows to the
// portal's configured field set (activeCollectNames); omitted, every declared field renders —
// the pre-config behavior, kept for surfaces with no portal in hand.
export function portalSectionFields(section: ReferralFieldSection, activeNames?: string[]): PortalSectionField[] {
  return PORTAL_COLLECT_FIELDS.filter((field) => field.section === section)
    .filter((field) => !activeNames || activeNames.includes(field.name))
    .flatMap((field) => sectionRowsFor(field, section))
    .sort((a, b) => a.order - b.order);
}

// The packet value name — documents, not a typeable field.
export const ATTACHMENTS_VALUE_NAME = 'attachments';

// Names that hold every value present rather than the first — the AI reaches for the
// *_all capture verbs for these.
const LIST_OUTPUT_NAMES: string[] = [ATTACHMENTS_VALUE_NAME, VALUE_NAMES.FACILITIES];

// One captured output, with the shape every mode needs to know about it. Callers that
// only want names map over `name`; the AI recording passes these through as its goal
// outputs, so per-output shape reaches the model without a second declaration.
export interface CollectOutput {
  name: string;
  cardinality: 'one' | 'many';
  // Set when this output is one alternative way to fill a single field: `satisfies` is the
  // field, `shape` is which alternative, and `description` says when that shape applies.
  // Outputs sharing a `satisfies` are exclusive — capturing every name in one shape
  // satisfies the field, so the AI renders them as one choice instead of a list of
  // outputs it could never all capture at once.
  satisfies?: string;
  shape?: string;
  description?: string;
  // The field's own label, carried on every shape of it: a row collapsed from several capture
  // names can't derive a name from any one of them ("Patient name", not "Patient first name").
  label?: string;
}

// The value names a script captures into for one collect field, with the shape metadata that
// says which of them are alternatives. A field with no `captureShapes` is captured under its
// own name.
function captureOutputsFor(field: ReferralFieldDef): Omit<CollectOutput, 'cardinality'>[] {
  if (!field.captureShapes) return [{ name: field.name }];
  return field.captureShapes.flatMap(({ shape, names, when }) =>
    names.map((name) => ({ name, satisfies: field.name, shape, description: when, label: field.label }))
  );
}

// The ONE portal-collection field set: the action's own outputs widened with the
// collect-fields, in a stable order. Record, replay, and AI recording all ask for
// exactly this list, so every mode shows the same rows (a replayed script that never
// captured a field shows its row unfilled — consistency over per-mode curation).
//
// A script captures into the engine's value names, so a field declaring `captureShapes`
// contributes those rather than itself — one output per name, carrying which shape it
// belongs to. The manual surfaces are the other projection of the same declaration: they
// render the FIELD (see PORTAL_COLLECT_FIELDS), because a person fills one name widget
// where a script extracts one or two values.
export function portalCollectionOutputs(produces: string[]): CollectOutput[] {
  const collected = PORTAL_COLLECT_FIELDS.flatMap(captureOutputsFor);
  const outputs = [
    ...produces.map((name) => ({ name })),
    ...collected.filter((output) => !produces.includes(output.name)),
  ];
  return outputs.map((output) => ({
    ...output,
    cardinality: LIST_OUTPUT_NAMES.includes(output.name) ? 'many' : 'one',
  }));
}

// A collected name, in whichever shape the portal put in front of the person filling it.
// Posted as the `patient_name` value; the api resolves it to the engine's first/last pair
// (Workflow::NormalizeNameOutput), so no lexical guessing happens in the browser.
export interface CollectedName {
  first?: string;
  last?: string;
  full?: string;
}

// Drop the composite entirely when nothing was entered — an absent optional value, not a
// blank one, so it never outranks what downstream extraction reads off the documents.
export function collectedNameValue(name: CollectedName): CollectedName | undefined {
  const first = name.first?.trim() ?? '';
  const last = name.last?.trim() ?? '';
  const full = name.full?.trim() ?? '';
  if (full) return { full };
  if (first || last) return { first, last };
  return undefined;
}

// A facility in the one shape every collection mode sends it, mirroring the api's
// PortalMapping::CollectedValue: `externalValue` is the portal's own string, which a script or
// the AI read off the page and the mapping step has to resolve; `mappedValue` is an Olio
// facility, which is what manual entry produces because a person picks it from a dropdown —
// already mapped, so the mapping step passes it through. camelCase like everything else this UI
// sends — the API's middleware snake-cases request bodies all the way down, value JSON included.
// Both halves together is a person answering the mapping up front: the api resolves the
// entry AND saves the pair as a PortalMapping, so future runs naming the same string pass
// through silently (PortalMapping::CollectedValue).
export type CollectedFacility = { externalValue: string; mappedValue?: string } | { mappedValue: string };

export const portalFacility = (externalValue: string): CollectedFacility => ({ externalValue });

export const pickedFacility = (groupId: string): CollectedFacility => ({ mappedValue: groupId });

export const mappedPortalFacility = (externalValue: string, groupId: string): CollectedFacility => ({
  externalValue,
  mappedValue: groupId,
});

// A facility a person may pick, wherever the options come from — the user's allowed facilities
// on the retrieval form, the client's provider groups in the mapping review and admin section.
export interface FacilityOption {
  id: string;
  name: string;
  groupType?: string;
}

// The `facilities` value as retrieval completion sends it: what the person picked, in order,
// with the empty rows dropped. An all-empty list is still sent — the mapping step gates on the
// value being present, then asks a person when there is nothing in it.
export function collectedFacilities(picks: (FacilityOption | null)[]): CollectedFacility[] {
  return picks.flatMap((pick) => (pick ? [pickedFacility(pick.id)] : []));
}

// How a collected facility reads to a person: the portal's own string, or the facility's name
// looked up from the options at hand. A pick carries only its id — storing a label alongside it
// would be a second copy of the facility's name, stale the moment someone renames the building.
export function collectedFacilityLabel(entry: unknown, options: { id: string; name: string }[] = []): string {
  const external = (entry as { externalValue?: unknown } | null)?.externalValue;
  if (typeof external === 'string') return external;
  const mapped = (entry as { mappedValue?: unknown } | null)?.mappedValue;
  return options.find((option) => option.id === mapped)?.name ?? 'facility';
}

// The facilities the extension's HUD picked, as it sends them: the values of the field Olio
// described to it, which for facilities are ids out of this user's own facility list. Turning
// them into CollectedFacility entries is this side's job — the HUD renders fields and returns
// what they hold, and knows nothing about what any of them mean.
export function pickedFacilityIds(collected: unknown): string[] {
  if (!Array.isArray(collected)) return [];
  return collected.filter((id): id is string => typeof id === 'string' && !!id);
}
